<template>
	<div>
		<!-- The teacher view of a student progress report should include the student name, programme, year and group / firm at the top. -->
		<div class="row">
            <div v-if="student" class="clearfix col-md-9">
                <h1>
                    {{ student.displayName }}
                    <button v-if="$UserSession.hasPriv.call(this, 'REPORT_ALLSTUDENTS')" class="btn btn-outline-dark btn-sm" @click="selectStudent()">{{ $t('change') }}</button>
                <div v-if="reportconfigs.length > 0" class="float-right form-inline">
                    <SelectInput
                        v-model="reportconfig_id"
                        :options="reportconfigs"
                        optionValue="id"
                        optionLabel="title"
                        :label="$t('download')"
                        :srOnly="true"
                    />
                    <button class="btn btn-outline-dark btn-sm ml-2" @click="downloadReport()">{{ $t('download') }}</button>
                </div>
                </h1>
                <ul class="list-inline">
                    <li class="list-inline-item">{{ $t('programme-colon') }} {{ $config.getProgrammeNameById(student.programme) }}</li>
                    <li class="list-inline-item">{{ $t('year-colon') }} {{ $config.getUserYearStr(student) }}</li>
                    <li class="list-inline-item">{{ $t('group-colon') }} {{ $config.getGroupNameById(student.group) }}</li>
                    <li class="list-inline-item">{{ $t('firm-colon') }} {{ $config.getFirmNameById(student.firm) }}</li>
                </ul>
            </div>
            <div class="col-md-3">
                <b-dropdown 
                    id="download" 
                    :text="$t('download')" 
                    class="m-md-2 float-right"
                    variant="primary"
                    v-if="$config.getUserYearStr(student) !== null && $UserSession.hasPriv.call(this, 'REPORT_ACTVITY_DOWNLOAD')"
                >
                    <b-dropdown-item 
                        v-for="year in studentYears(student)"
                        :key="year"
                        @click="downloadReport('pdf', Number($config.getUserYearStr(student) - year))"
                    >
                        {{$t('year')}} {{ year }} 
                    </b-dropdown-item>
                    
                </b-dropdown>
            </div>
        </div>
		<div v-if="$UserSession.hasPriv.call(this, 'REPORT_ALLSTUDENTS') && tests.length">
			<div class="row">
				<div class="col-md-4">
					<MultiselectSearchInput
						:label="$t('tests')"
						:options="tests"
						optionValue="id"
						optionLabel="name"
						v-model="search.test"
						@input="getReport()"
					/>
				</div>
			</div>
		</div>

		<div v-if="!loading">
			<div class="mb-4 row">
				<div class="col-12 col-xl-8 mb-4">
					<div class="list-group border-radius-2 no-border">
						<div class="list-group-item font-weight-bold">{{ $t('absences') }}</div>
						<div v-for="absence in report.absences" :key="absence.AbsenceID" class="list-group-item py-2 text-sm">
							<div class="row">
								<span class="col-2">
									{{ $datetime.getLocaleStringFromISODate(absence.DateAbsent) }}<br>
									{{ absence.DateAbsent.split(' ')[1].slice(0, 5) }}
								</span>
								<span class="col-3">{{ clinics[absence.ClinicID] ? clinics[absence.ClinicID].Name : '-' }}</span>
								<span class="col-2">{{ absence.Teacher }}</span>
								<span class="col-3">{{ absence.AbsenceComment }}</span>
								<span class="col-2" v-if="absence.DateCreated">
									{{ $datetime.getLocaleStringFromISODate(absence.DateCreated) }}<br>
									{{ absence.DateCreated.split(' ')[1].slice(0, 5) }}
								</span>
							</div>
						</div>
					</div>
				</div>

				<div class="col-12 col-xl-4 mb-4">
					<div class="list-group border-radius-2 no-border">
						<div class="list-group-item font-weight-bold">{{ $t('teachers-seen') }}</div>
						<div class="d-flex list-group-item text-sm font-weight-bold">
							<span class="w-50">{{ $t('name') }}</span>
							<span class="w-50">{{ $t('times-seen') }}</span>
						</div>
						<div v-for="count, id in report.teachercount" :key="id" class="d-flex list-group-item py-2 text-sm">
							<span class="w-50">{{ count.name }}</span>
							<span class="w-50">{{ count.count }}</span>
						</div>
					</div>
				</div>

				<div class="col-12 mb-4" v-if="report.competencies.length">
					<Table
						:data="report.competencies"
						:cols="[
							{ key: 'Code', heading: $t('code'), size: 2 },
							{ key: 'Name', heading: $t('name'), size: 3 },
							{ key: 'taken', heading: $t('num-times-taken'), size: 2 },
							{ key: 'failed', heading: $t('num-times-failed'), size: 2 },
							{ key: 'datepassed', heading: $t('passed-question'), size: 2, cb: (r) => r.datepassed ? $datetime.getLocaleStringFromISODate(r.datepassed) : '' },
						]"
						headerClass="text-sm py-2"
						rowClass="text-sm py-2"
						class="mb-0"
					>
						<template v-slot:header>
							<div class="list-group-item font-weight-bold border-bottom border-light">{{ $t('tests') }}</div>
						</template>
					</Table>
				</div>

				<div class="col-12 mb-4">
					<div class="list-group border-radius-2 no-border">
						<div class="list-group-item font-weight-bold">{{ $t('no-patients-booked') }}</div>
						<div v-for="date, i in report.nopatientsbooked" :key="i" class="list-group-item py-2 text-sm">
							{{ $datetime.getLocaleStringFromISODate(date.date) }} @ {{ date.date.split(' ')[1].slice(0, 5) }}
						</div>
					</div>
				</div>

				<div class="col-12 mb-4">
					<div class="list-group border-radius-2 no-border">
						<div class="list-group-item font-weight-bold">{{ $t('overview') }}</div>
						<div v-for="heading, i in report.reportsummaryheadings" :key="i" class="list-group-item">
							<div class="font-weight-bold mb-2">{{ heading.heading }}</div>
							<div class="d-flex">
								<div v-for="col, j in heading.cols" :key="`${i}-${j}`" class="p-2 border border-light shadow-sm mr-2 rounded flex-fill flex-grow-1">
                                    <div class="text-sm font-weight-bold mb-1">{{ col.caption }}</div>
									<div v-if="col.type === 'count'" class="badge badge-info text-lg mt-1">{{ report.totals[col.data] }}</div>
									<div v-else-if="col.type === 'grade'" class="d-flex">
										<div v-for="v, k in report.totals['Grades'][col.data]" :key="`${i}-${j}-${k}`" class="flex-grow-1 pr-2">
											<div class="badge badge-primary">{{ getGradeCaption(col, k) }}</div>
											<div>{{ v }}</div>
										</div>
									</div>
									<div v-else-if="col.type === 'type'" class="badge badge-info h3 mb-0">{{ report.totals['Types'][col.data] }}</div>
									<div v-else-if="col.type === 'oknotok'" class="d-flex">
										<div class="flex-grow-1 mr-2">
											<div class="mb-1">{{ $t('ok-activity-report') }}</div>
											<div class="badge badge-success h3 mb-0">{{ report.totals['OkNotOk'][col.data]['OK'] }}</div>
										</div>
										<div class="flex-grow-1 mr-2">
											<div class="mb-1">{{ $t('not-ok-activity-report') }}</div>
											<div class="badge badge-danger h3 mb-0">{{ report.totals['OkNotOk'][col.data]['NotOK'] }}</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="mb-4">
				<div class="list-group border-radius-2 no-border mb-3">
					<div class="list-group-item font-weight-bold">{{ $t('treatment-sessions') }}</div>
				</div>
				<div v-for="(session, id) in report.treatmentsessions" :key="id" class="list-group border-radius-2 no-border mb-3">
					<div class="list-group-item">
						<span class="mr-3">{{ id }} - {{ $datetime.getLocaleStringFromISODate(session.DateOfTreatment, "datetime") }}</span>
						<span class="mr-3">{{ (sessiontypes[session.TypeId]?sessiontypes[session.TypeId].name:'') }}</span>
						<span class="mr-3">{{ (specialties[session.SpecialtyId]?specialties[session.SpecialtyId].name:'') }}</span>
						<span class="mr-3">{{ (clinics[session.ClinicID]?clinics[session.ClinicID].name:'') }}</span>
					</div>
					<div class="list-group-item d-flex flex-wrap justify-content-between" style="gap: 0.8rem">
						<div v-for="field in assessmentfields.markingsupp.filter(f => f.values)" :key="field.id">
							<div class="text-sm font-weight-bold">
								{{ field.caption }}
							</div>
							<div>
								<span class="btn btn-sm" :class="getMarkingClass(field, session)" v-if="getMarkingValue(field, session)">{{ getMarkingValue(field, session) }}</span>
								<template v-else>-</template>
							</div>
						</div>
					</div>
					<div class="list-group-item d-flex flex-wrap justify-content-between" style="gap: 0.8rem" v-if="assessmentfields.markingsummary">
						<div v-for="field in assessmentfields.markingsummary.filter(f => f.values)" :key="field.id">
							<div class="text-sm font-weight-bold">
								{{ field.caption }}
							</div>
							<div>
								<span class="btn btn-sm" :class="getMarkingClass(field, session)" v-if="getMarkingValue(field, session)">{{ getMarkingValue(field, session) }}</span>
								<template v-else>-</template>
							</div>
						</div>
					</div>
					<div class="list-group-item d-flex flex-column">
						<div v-if="session.PatientReference" class="row mb-2">
							<span class="col-4 col-lg-3 font-weight-bold">{{ $t('patient-colon') }}</span>
							<span class="col-8 col-lg-9">
								{{ session.PatientReference }}
								<span v-if="patientattendances[session.PatientAttendance]"> - {{ patientattendances[session.PatientAttendance].title }}</span>
							</span>
						</div>
						<div class="row mb-2">
							<span class="col-4 col-lg-3 font-weight-bold">{{ $t('student-colon') }}</span>
							<span class="col-8 col-lg-9">{{ studentname }}</span>
						</div>
						<div class="row">
							<span class="col-4 col-lg-3 font-weight-bold">{{ $t('teacher-colon') }}</span>
							<span class="col-8 col-lg-9">{{ session.teachername }}</span>
						</div>
					</div>
					<div class="list-group-item" v-for="procedure, p_id in session.procedures" :key="`${id}_${p_id}`">
						<div class="row">
							<div class="font-weight-bold text-sm col-4 col-lg-3">{{ procedure.Caption }}</div>
							<div class="col-8 col-lg-9 d-flex flex-row flex-wrap" style="gap: 0.5rem">
								<div v-for="el, e_id in procedure.formElements" :key="`${id}_${p_id}_${e_id}`" class="badge badge-dark mw-100" :class="{ 'd-none': !el.value }" style="overflow: hidden; text-overflow: ellipsis">
									{{ el.caption ? `${el.caption}: ${el.value}` : `${el.value}` }}
								</div>
								<div v-for="field in proceduremarking" :key="field.id" class="badge badge-info">
									<div v-if="procedure[field.type] && procedure[field.type][field.id]">
										{{ field.caption }}: {{ field.values.filter(v => v.value === procedure[field.type][field.id])[0].caption }}
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="list-group-item">
						<template v-for="markinggroup in assessmentfields">
							<template v-for="field in markinggroup.filter(f => !f.values)">
								<div class="row mb-2" :key="field.id" v-if="getNonMarkingText(field,session) !== null">
									<span class="col-4 col-lg-3 font-weight-bold">{{ `${field.caption}:` }}</span>
									<span class="col-8 col-lg-9">{{ getNonMarkingText(field,session) }}</span>
								</div>
							</template>
                        </template>
						<!--div class="row mb-2">
							<span class="col-4 col-lg-3 font-weight-bold">{{ $t('tutor-feedback-colon') }}</span><span class="col-8 col-lg-9">{{ session.TeacherComment }}</span>
						</div>
                        <div v-if="session.laFeedback" class="row">
                            <span class="col-4 col-lg-3 font-weight-bold">{{ $t('la-feedback') }}</span><span class="col-8 col-lg-9">{{ session.laFeedback }}</span>
                        </div>
						<div class="row">
							<span class="col-4 col-lg-3 font-weight-bold">{{ $t('student-reflections-colon') }}</span><span class="col-8 col-lg-9">{{ session.StudentComment }}</span>
						</div-->
					</div>
					<!--div v-if="session.Commendation" class="list-group-item">
						<div class="row mb-2">
							<span class="col-4 col-lg-3 font-weight-bold">{{ $t('commendation') }}</span><span class="col-8 col-lg-9">{{ session.CommendationReason ? session.CommendationReason : $t('no-reason') }}</span>
						</div>
					</div>
					<div v-if="session.CauseForConcern" class="list-group-item">
						<div class="row mb-2">
							<span class="col-4 col-lg-3 font-weight-bold">{{ $t('cause-for-concern') }}</span><span class="col-8 col-lg-9">{{ session.CauseForConcernReason ? session.CauseForConcernReason : $t('no-reason') }}</span>
						</div>
					</div-->

					<template v-if="session.tests.length">
						<div class="list-group-item">
							<h3 class="m-0">{{ $t('tests') }}</h3>
						</div>
						<div class="list-group-item" v-for="(test, id) in session.tests" :key="id">
							<strong>{{test.code}}
							{{test.title}}</strong>
							<span v-if="test.HasPassed==1" class="btn btn-sm btn-success ml-2">{{ $t('passed') }}</span>
							<span v-else-if="test.HasPassed==-1" class="btn btn-sm btn-danger ml-2">{{ $t('failed') }}</span>
							<span v-else class="btn btn-sm btn-dark ml-2">{{ $t('not-marked') }}</span>

							<template v-if="test.form">
								<template v-for="(page, pageIndex) in test.form.pages">
									<div :key="pageIndex">
										<template v-if="page.title">{{ page.title }}</template>
										<FormGrid :page="page" :readonly="true" :viewAs="student"></FormGrid>
									</div>
								</template>
							</template>
						</div>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import FormGrid from "src/components/FormEditor/FormGrid";;
import Table from "src/components/Table/Table";

export default {
	components: {
		FormGrid,
		Table,
	},
	data() {
		return {
			search: {
				student_id: null,
				test: []
			},
			loading: true,
			reportconfigs: [],
			reportconfig_id: null,
			report: {
				treatmentsessions: {},
				absences: [],
				competencies: [],
				nopatientsbooked: [],
				reportsummaryheadings: [],
				teachercount: [],
				totals: [],
			},
			patientattendances: {},
			tests: [],
			clinics: {},
			sessiontypes: {},
			specialties: {},
			proceduremarking: {},
			assessmentfields: {},
			student: {},
			studentname: '',
            date:{
                from: null,
                to: null
            },
            downloadactivityreport: false
		}
	},
	mounted() {
		this.getAvailableConfigs();
		if(this.$UserSession.hasPriv('REPORT_ALLSTUDENTS')){
			if(this.$route.params.studentid) {
				this.search.student_id = this.$route.params.studentid
				this.getStudentDetails();
			} else {
				this.selectStudent()
			}
		} else {
			this.search.student_id = this.$UserSession.getValue('id');
			this.getStudentDetails();
		}
		this.getAll()
	},
	methods: {
		getStudentDetails() {
			if(this.search.student_id){
				this.$users.getUserById.call(this, this.search.student_id).then(student => {
					this.student = student;
				});
				this.$users.getDisplayName.call(this, this.search.student_id).then(name => {
					this.studentname = name
				})
			}
		},
		unableToGetSettings() {
			const self = this
			this.$store.commit("openDialog" , {
				title: this.$t('something-went-wrong'),
				content: this.$t('unable-to-get-report-settings'),
				confirmCallBack: function(){
					self.$router.replace('/')
				}
			})
		},
		selectStudent() {
			const self = this
			this.$users.getStudents.call(this).then(students => {
				this.$store.commit("openModal", {
					type: "selector",
					title: this.$t("select-a-student"),
					selectCallback: function(selection) {
						self.$store.commit("closeModal")
						self.search.student_id = selection.id;
						self.getStudentDetails();
						self.getReport()
					},
					selected: [
					],
					data: students,
					captionKey: "displayName",
                    subcaptionKey: "studentid",
					studentSearchFilters: true
				})
			})
		},
		getAvailableConfigs() {
			this.$cfetch(`${this.$settings.APIBaseURL}/reporting/list`, {
				data: {
					all: true,
					reportconfig_type: 'activity'
				}
			})
			.then(json => {
				if (json.status === "SUCCESS") {
					this.reportconfigs = json.reportconfigs
				}
			});
		},
		getAll() {
			return Promise.all([
				this.getSpecialties(),
				//this.getClinics(),
				this.getTests(),
				this.getPatientAttendances(),
				this.getSessionTypes(),
				this.getReport()
			]).then(() => {
				this.loading = false
			}).catch(error => {
                this.loading = false 
                console.error("Error loading data:", error) 
            })
		},
		getSpecialties() {
			this.specialties = this.$config.getValue("specialties").reduce((prev, curr) => {
				prev[curr.id] = curr
				return prev
			}, {})

			return Promise.resolve()
		},
		getClinics() {
			this.clinics = this.$config.getValue("clinics").reduce((prev, curr) => {
				prev[curr.id] = curr
				return prev
			}, {});

			return Promise.resolve();
			/*
			return this.$cfetch(`${this.$settings.APIBaseURL}/clinics/all`, {
				method: 'GET',
			}).then(res => {
				if (res.status !== 'SUCCESS') throw res

				this.clinics = res.results.reduce((prev, curr) => {
					prev[curr.ClinicID] = curr
					return prev
				}, {})
			}).catch(err => {
				this.$log.error.call(this, err)
				this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                })
			})
			*/
		},
		getTests() {
			return this.$cfetch(`${this.$settings.APIBaseURL}/competencytests/test/list`, {
				method: 'GET',
			}).then(res => {
				if (res.status !== 'SUCCESS') throw res
				this.tests = res.competencytests;
			}).catch(err => {
				this.$log.error.call(this, err)
				this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                })
			})
		},
		getPatientAttendances() {
			return this.$cfetch(`${this.$settings.APIBaseURL}/patientattendances/all`, {
				method: 'GET',
			}).then(res => {
				if (res.status !== 'SUCCESS') throw res

				this.patientattendances = res.results.reduce((prev, curr) => {
					prev[curr.id] = curr
					return prev
				}, {})
			}).catch(err => {
				this.$log.error.call(this, err)
				this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                })
			})
		},
		getSessionTypes() {
			this.sessiontypes = this.$config.getValue('participationTypes').reduce((prev, curr) => {
				prev[curr.id] = curr
				return prev
			}, {});
        },
		getReport(){
			this.$store.commit("showBlockingSpinner")

			var data = {}
			if(this.$UserSession.hasPriv('REPORT_ALLSTUDENTS')){
				if(this.search.student_id) {
					data = JSON.parse(JSON.stringify(this.search));
				} else {
					return Promise.reject()
				}
			}
			for(var key in this.$route.query){
				if(key != 'username' && key != 'password' && key != 'authToken'){
					data[key] = this.$route.query[key]
				}
			}
			if(data.test){
				data.test = data.test.map(item => item.id);
			}

			return this.$cfetch(`${this.$settings.APIBaseURL}/reporting/activity/individual`, {
                method: 'POST',
				cache: false,
				data
			})
			.then(res => {
				if (res.status !== 'SUCCESS') {
					throw new Error("Could not fetch report settings")
				}
                if (!res.result?.treatmentsessions || 
                (Array.isArray(res.result.treatmentsessions) && res.result.treatmentsessions.length === 0) ||
                (typeof res.result.treatmentsessions === 'object' && Object.keys(res.result.treatmentsessions).length === 0)) {
                    console.warn('⚠️ treatmentsessions is empty!', res.result);
                }

				this.report.treatmentsessions = res.result.treatmentsessions
				this.report.absences = res.result.absences
				this.report.competencies = res.result.competencies
				this.report.nopatientsbooked = res.result.nopatientsbooked
				this.report.reportsummaryheadings = res.result.reportsummaryheadings
				this.report.teachercount = res.result.teachercount
				this.report.totals = res.result.totals
				this.proceduremarking = res.result.proceduremarking
				this.assessmentfields = res.result.assessmentfields
                this.downloadactivityreport = res.result.downloadactivityreport


				Object.keys(this.report.treatmentsessions).forEach(id => {
                    this.report.treatmentsessions[id].teachername = ''
					this.$users.getDisplayName.call(this, this.report.treatmentsessions[id].TeacherID).then(name => {
						this.report.treatmentsessions[id].teachername = name
					})
				})

				Object.keys(this.report.teachercount).forEach(id => {
					this.$users.getDisplayName.call(this, id).then(name => {
                        
						this.report.teachercount[id] = {
							id,
							name,
							count: this.report.teachercount[id],
						}
					})
				})

				this.loading = false;
			})
			.catch(err => {
				this.unableToGetSettings()
                
				this.$log.error.call(this, err)
			}).finally(() => {
				this.$store.commit("hideBlockingSpinner")
			})
		},
		downloadReport(type, years_into_past = null){
            
            if(years_into_past !== null){
                
                const today = new Date();
                const baseYear = today.getFullYear() - years_into_past;

                // the standard individual report expects the date in d/m/Y H:i:s format
                const formatDate = (date_obj) => {
                    
                    const day = date_obj.getDate(); 
                    const month = date_obj.getMonth() + 1;
                    const year = date_obj.getFullYear(); 

                    const formattedDay = day < 10 ? '0' + day : day;
                    const formattedMonth = month < 10 ? '0' + month : month;

                    return `${formattedDay}/${formattedMonth}/${year}`;
                }

                this.date.from = formatDate(new Date(baseYear-1, 7, 31)); // 31 August
                this.date.to = formatDate(new Date(baseYear, 8, 31)); // 31 September
            }
			var body = {
				username: this.$UserSession.getValue.call(this, "username"),
				authToken: this.$UserSession.getValue.call(this, "authToken"),
				student_id: this.student.id,
				reportconfig_id: this.reportconfig_id,
                download: type,
                date_from: this.date.from,
                date_to: this.date.to
			}

			const url = `${this.$settings.APIBaseURL}/reporting/activity/individual?` + new URLSearchParams(body).toString();
    		window.open(url);

			/*
			var download = {}
			if(this.$UserSession.hasPriv('REPORT_ALLSTUDENTS')){
				if(this.search.student_id) {
					download = JSON.parse(JSON.stringify(this.search));
				} else {
					return Promise.reject()
				}
			}
			for(var key in this.$route.query){
				if(key != 'username' && key != 'password' && key != 'authToken'){
					download[key] = this.$route.query[key]
				}
			}
			if(download.test){
				download.test = download.test.map(item => item.id);
			}
			download.download = 'csv';


			var body = {
				username: this.$UserSession.getValue.call(this, "username"),
				authToken: this.$UserSession.getValue.call(this, "authToken"),
				download: JSON.stringify(download)
			}

			const url = `${this.$settings.APIBaseURL}/reporting/activity/individual?` + new URLSearchParams(body).toString();
    		window.open(url);
    		*/

        },
        getGradeCaption(col, value){
            const grade = (col.grades || []).find(g => (typeof g === 'object' ? g.value : g) == value);
            return (grade && typeof grade === 'object' && grade.caption) ? grade.caption : value;
        },
		getMarkingValue(field, session){
			if(field.type == 'oknotok'){
				if(session[field.id] == 1){
					return 'OK';
				} else if(session[field.id] == -1) {
					return 'Not Ok';
				}
			} else {
				if(session[field.id]){
					return field.values.filter(v => v.value == session[field.id]).length ? field.values.filter(v => v.value == session[field.id])[0].caption : session[field.id];
				}
			}
		},
		getMarkingClass(field, session){
			if(field.type == 'oknotok'){
				if(session[field.id] == 1){
					return 'btn-success';
				} else if(session[field.id] == -1) {
					return 'btn-danger';
				}
			}
			var selected = field.values.filter(v => v.value == session[field.id]).length ? field.values.filter(v => v.value == session[field.id])[0] : null;
			if(selected && selected.cssclass){
				return 'btn-on ' + selected.cssclass;
			}
			return 'btn-primary';
		},
        getNonMarkingText(field,session){

			if(field.type == 'textarea'){
				if(
					session[field.id]
					&& session[field.id] !== null
				){
					return session[field.id]
				}

				if(
					session[field.name]
					&& session[field.name] !== null
				){
					return session[field.name]
				}
            }
            return null;
        },
        studentYears(student){
           return Array.from({ length: Number(this.$config.getUserYearStr(student)) }, (_, i) => i + 1);
        }
	}
}
</script>
